// App.js - Import useAutoLogout dari folder components
import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import Dashboard from './components/Dashboard';
import DataPengiriman from './components/DataPengiriman';
import KelolaAkun from './components/KelolaAkun';
import StatusPengiriman from './components/StatusPengiriman';
import useAutoLogout from './components/useAutoLogout'; // Import dari folder components
import { jwtDecode } from 'jwt-decode';
import './App.css';

// KOMPONEN UNTUK HALAMAN PUBLIC TRACKING
function PublicTrackingPage() {
  return <LandingPage onLogin={() => {}} isPublicOnly={true} />;
}

// KOMPONEN UNTUK HALAMAN LOGIN ADMIN (URL Tersembunyi)
function SecureAdminLogin({ onLogin }) {
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockTimer, setBlockTimer] = useState(0);
  
  useEffect(() => {
    // Cek jika sedang di-block
    const blockData = localStorage.getItem('admin_login_block');
    if (blockData) {
      const { until, attempts } = JSON.parse(blockData);
      const now = Date.now();
      if (now < until) {
        setIsBlocked(true);
        setBlockTimer(Math.ceil((until - now) / 1000));
        setLoginAttempts(attempts);
      } else {
        localStorage.removeItem('admin_login_block');
      }
    }
  }, []);

  useEffect(() => {
    if (isBlocked && blockTimer > 0) {
      const timer = setTimeout(() => {
        setBlockTimer(prev => {
          if (prev <= 1) {
            setIsBlocked(false);
            setLoginAttempts(0);
            localStorage.removeItem('admin_login_block');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isBlocked, blockTimer]);

  const handleLoginAttempt = (success) => {
    if (success) {
      setLoginAttempts(0);
      localStorage.removeItem('admin_login_block');
      return;
    }

    const newAttempts = loginAttempts + 1;
    setLoginAttempts(newAttempts);

    // Setelah 5 kali gagal, block selama 15 menit
    if (newAttempts >= 5) {
      const blockUntil = Date.now() + (15 * 60 * 1000); // 15 menit
      localStorage.setItem('admin_login_block', JSON.stringify({
        until: blockUntil,
        attempts: newAttempts
      }));
      setIsBlocked(true);
      setBlockTimer(15 * 60);
    }
  };

  const wrappedOnLogin = (token, userData) => {
    handleLoginAttempt(true);
    onLogin(token, userData);
  };

  if (isBlocked) {
    const minutes = Math.floor(blockTimer / 60);
    const seconds = blockTimer % 60;
    
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0d4499 0%, #1055C9 100%)'
      }}>
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '20px',
          textAlign: 'center',
          maxWidth: '400px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>🔒</div>
          <h2 style={{ color: '#dc2626', marginBottom: '15px' }}>Akses Diblokir</h2>
          <p style={{ color: '#4a5568', marginBottom: '20px', lineHeight: '1.6' }}>
            Terlalu banyak percobaan login gagal.<br/>
            Silakan tunggu <strong>{minutes}:{seconds.toString().padStart(2, '0')}</strong> menit
          </p>
          <p style={{ fontSize: '14px', color: '#718096' }}>
            Total percobaan: {loginAttempts}/5
          </p>
        </div>
      </div>
    );
  }

  return (
    <LandingPage 
      onLogin={wrappedOnLogin} 
      isPublicOnly={false}
      onLoginFailed={() => handleLoginAttempt(false)}
      remainingAttempts={5 - loginAttempts}
    />
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('lastActivity');
    setIsAuthenticated(false);
    setUser(null);
  }, []);

  const checkAuth = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
          handleLogout();
        } else {
          setIsAuthenticated(true);
          setUser(decoded);
        }
      } catch (error) {
        handleLogout();
      }
    } else {
      setIsAuthenticated(false);
    }
    setLoading(false);
  }, [handleLogout]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('lastActivity', Date.now().toString());
    const decoded = jwtDecode(token);
    setIsAuthenticated(true);
    setUser(decoded);
  };

  // Gunakan hook auto-logout (dari folder components)
  useAutoLogout(handleLogout, isAuthenticated);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* HALAMAN PUBLIC - Hanya untuk tracking */}
        <Route 
          path="/" 
          element={
            !isAuthenticated ? 
              <PublicTrackingPage /> : 
              <Navigate to="/dashboard" />
          } 
        />

        {/* URL TERSEMBUNYI UNTUK ADMIN LOGIN */}
        <Route 
          path="/admin" 
          element={
            !isAuthenticated ? 
              <SecureAdminLogin onLogin={handleLogin} /> : 
              <Navigate to="/dashboard" />
          } 
        />

        {/* ALTERNATIF: Tambahkan URL cadangan jika lupa URL utama */}
        <Route 
          path="/backup-admin-access" 
          element={
            !isAuthenticated ? 
              <SecureAdminLogin onLogin={handleLogin} /> : 
              <Navigate to="/dashboard" />
          } 
        />

        {/* Rute Dashboard */}
        <Route 
          path="/dashboard" 
          element={
            isAuthenticated ? 
              <Dashboard user={user} onLogout={handleLogout} /> : 
              <Navigate to="/" />
          } 
        />

        {/* Rute Data Pengiriman */}
        <Route
          path="/pengiriman"
          element={
            isAuthenticated ?
              <DataPengiriman user={user} onLogout={handleLogout} /> :
              <Navigate to="/" />
          }
        />

        {/* Rute Kelola Akun */}
        <Route
          path="/kelolaakun"
          element={
            isAuthenticated ?
              <KelolaAkun user={user} onLogout={handleLogout} /> :
              <Navigate to="/" />
          }
        />

        {/* Rute Status Pengiriman */}
        <Route
          path="/status-pengiriman"
          element={
            isAuthenticated ?
              <StatusPengiriman user={user} onLogout={handleLogout} /> :
              <Navigate to="/" />
          }
        />

        {/* Catch all route - redirect ke home */}
        <Route 
          path="*"
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/"} />} 
        />
      </Routes>
    </Router>
  );
}

export default App;